import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import {
  useInformarPagamento,
  useParcelasPendentes,
  type ValeParcelaPendente,
} from './pagamento.api';

export interface InformarPagamentoDialogProps {
  open: boolean;
  employeeId: number;
  notaFiscalId: number;
  colaboradorNome: string;
  clienteNome: string;
  periodo: string;
  valorReceber: number;
  onClose: () => void;
}

function keyOf(p: ValeParcelaPendente): string {
  return `${p.valeCodigo}-${p.id}`;
}
function formatBRL(v: number): string {
  return (Number.isFinite(v) ? v : 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}
function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

/**
 * "Informar pagamento" da NF colaborador (tela `ColaboradorPagamentoNF`): marca
 * a NF como paga e desconta as parcelas de vale pendentes selecionadas (vincula
 * cada uma à NF). Sem parcela pendente → só conferência (confirma o pagamento).
 * Card [PiEuG4Wx]. As mutações reusam `/employees/:id/...` (mock até publicar).
 */
export function InformarPagamentoDialog({
  open,
  employeeId,
  notaFiscalId,
  colaboradorNome,
  clienteNome,
  periodo,
  valorReceber,
  onClose,
}: InformarPagamentoDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const pendentesQuery = useParcelasPendentes(employeeId, open);
  const informar = useInformarPagamento(employeeId, notaFiscalId);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());

  const pendentes = pendentesQuery.data ?? [];

  function toggle(p: ValeParcelaPendente): void {
    setSelecionadas((prev) => {
      const next = new Set(prev);
      const k = keyOf(p);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  const totalDesconto = pendentes
    .filter((p) => selecionadas.has(keyOf(p)))
    .reduce((acc, p) => acc + p.valor, 0);

  async function confirmar(): Promise<void> {
    const parcelas = pendentes
      .filter((p) => selecionadas.has(keyOf(p)))
      .map((p) => ({ valeCodigo: p.valeCodigo, parcelaCodigo: p.id }));
    try {
      await informar.mutateAsync({ parcelas });
      toast.success(t('features.colaboradores.notasFiscais.pagamento.sucesso'));
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const A = 'features.colaboradores.notasFiscais.pagamento';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setSelecionadas(new Set()) }}
    >
      <DialogTitle>{t(`${A}.title`)}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle1">{colaboradorNome}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`${A}.resumo`, {
                cliente: clienteNome,
                periodo,
                valor: formatBRL(valorReceber),
              })}
            </Typography>
          </Box>
          <Divider />

          {pendentesQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : pendentes.length === 0 ? (
            <Alert severity="info" variant="outlined">
              {t(`${A}.semParcelas`)}
            </Alert>
          ) : (
            <>
              <Typography variant="subtitle2">{t(`${A}.selecioneParcelas`)}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t(`${A}.colVale`)}</TableCell>
                    <TableCell>{t(`${A}.colVencimento`)}</TableCell>
                    <TableCell align="right">{t(`${A}.colValor`)}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendentes.map((p) => {
                    const k = keyOf(p);
                    return (
                      <TableRow key={k} hover onClick={() => toggle(p)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selecionadas.has(k)} />
                        </TableCell>
                        <TableCell>
                          {t(`${A}.valeParcela`, { vale: p.valeCodigo, parcela: p.id })}
                        </TableCell>
                        <TableCell>{formatDate(p.dataVencimento)}</TableCell>
                        <TableCell align="right">{formatBRL(p.valor)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Typography variant="body2" align="right" sx={{ fontWeight: 600 }}>
                {t(`${A}.totalDesconto`, { valor: formatBRL(totalDesconto) })}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={informar.isPending}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={() => void confirmar()} disabled={informar.isPending}>
          {pendentes.length === 0 ? t(`${A}.confirmarConferencia`) : t(`${A}.confirmar`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
